import {getDownloadURL, ref, uploadBytes} from 'firebase/storage';
import {storage} from './firebase';

const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export class ImageUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageUploadError';
  }
}

/**
 * Upload an image file to Firebase Storage and return the download URL.
 * @param file The file to upload
 * @param storagePath The path in Storage (e.g. 'conversation_images/feedbackId')
 * @returns The download URL of the uploaded image
 */
export async function uploadImage(file: File, storagePath: string): Promise<string> {
  // Validate size
  if (file.size > MAX_SIZE) {
    throw new ImageUploadError('Image must be smaller than 5MB');
  }

  // Validate type
  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new ImageUploadError('Only PNG, JPEG, GIF, and WebP images are allowed');
  }

  // Generate unique filename
  const ext = file.name.split('.').pop() || 'png';
  const filename = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${ext}`;
  const fullPath = `${storagePath}/${filename}`;

  // Upload
  const storageRef = ref(storage, fullPath);
  const metadata = {contentType: file.type};
  await uploadBytes(storageRef, file, metadata);

  // Get download URL
  return getDownloadURL(storageRef);
}
